<script>
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import Banner from 'dashboard/components/ui/Banner.vue';
import { createClient } from '@supabase/supabase-js';

export default {
  components: { Banner },
  props: {
    tableName: { type: String, default: 'announcements' },
  },
  setup(props) {
    useI18n();
    const { isAdmin } = useAdmin();
    const announcements = ref([]);
    const loading = ref(false);
    const userDismissedBanners = ref([]);

    // Supabase credentials
    const SUPABASE_URL = 'https://bwrfbiummnbyijkcwcfi.supabase.co';
    const SUPABASE_ANON_KEY =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJ3cmZiaXVtbW5ieWlqa2N3Y2ZpIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc3NDk3MTA4MiwiZXhwIjoyMDkwNTQ3MDgyfQ.XkFhSUMvWnfwh9gPv-Oll9Y5o687LPkkSOT3dtYHxWo';

    // Initialize Supabase client
    const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

    // Fetch announcements from Supabase
    const fetchAnnouncements = async () => {
      loading.value = true;
      try {
        const { data, error } = await supabase
          .from(props.tableName)
          .select('*')
          .eq('active', true)
          .order('created_at', { ascending: false });

        if (error) {
          return;
        }

        announcements.value = data || [];
      } catch (error) {
        /* empty */
      } finally {
        loading.value = false;
      }
    };

    // Load dismissed banners from localStorage
    const loadDismissedBanners = () => {
      userDismissedBanners.value =
        LocalStorage.get(LOCAL_STORAGE_KEYS.DISMISSED_ANNOUNCEMENTS) || [];
    };

    // Get the first active announcement that hasn't been dismissed and matches user role
    const activeAnnouncement = computed(() => {
      if (announcements.value.length === 0) {
        return null;
      }

      return announcements.value.find(announcement => {
        const isDismissed = userDismissedBanners.value.includes(
          announcement.id
        );
        const isActive = announcement.active;
        // Check if announcement targets this user's role
        const targetRoles = announcement.target_roles || ['admin'];
        const userRole = isAdmin.value ? 'admin' : 'agent';
        const matchesRole = targetRoles.includes(userRole);

        return !isDismissed && isActive && matchesRole;
      });
    });

    // Dismiss a banner
    const dismissBanner = announcementId => {
      if (!userDismissedBanners.value.includes(announcementId)) {
        userDismissedBanners.value.push(announcementId);
        LocalStorage.set(
          LOCAL_STORAGE_KEYS.DISMISSED_ANNOUNCEMENTS,
          userDismissedBanners.value
        );
      }
    };

    onMounted(() => {
      loadDismissedBanners();
      fetchAnnouncements();

      // Set up real-time subscription to Supabase changes (Realtime v2)
      const subscription = supabase
        .channel(`public:${props.tableName}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: props.tableName,
          },
          () => {
            fetchAnnouncements();
          }
        )
        .subscribe(() => {});

      return () => {
        supabase.removeChannel(subscription);
      };
    });

    return {
      activeAnnouncement,
      dismissBanner,
      loading,
      isAdmin,
    };
  },
};
</script>

<template>
  <Banner
    v-if="activeAnnouncement"
    :color-scheme="activeAnnouncement.color_scheme || 'primary'"
    :banner-message="activeAnnouncement.message"
    :href-link="activeAnnouncement.link_url || null"
    :href-link-text="activeAnnouncement.link_text || null"
    has-close-button
    @close="dismissBanner(activeAnnouncement.id)"
  />
</template>
